import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const HIGHLIGHT_CLASSES = [
  "ring-2",
  "ring-primary",
  "ring-offset-2",
  "rounded-md",
  "transition-shadow",
  "duration-500",
];
const HIGHLIGHT_DURATION_MS = 2000;
const SCROLL_DELAY_MS = 50;

/**
 * When a settings page mounts (or its hash changes), look up the element
 * matching `location.hash`, scroll it into view, and pulse a ring around it
 * so the user can see the result of a search-driven navigation.
 *
 * Mounted once at the SettingsLayout level — every section page benefits
 * automatically as long as its searchable fields render an element with the
 * matching `id` attribute.
 */
export function useSearchHighlight(): void {
  const { hash, key } = useLocation();

  useEffect(() => {
    // Reference `key` so the effect re-fires on every navigation, even when
    // hash stays the same — needed when user re-selects the same search
    // result from the palette.
    void key;
    if (!hash || hash.length <= 1) return;

    const anchorId = hash.slice(1);
    // Defer to next tick so the section's content has had a chance to mount
    // (queries resolve, conditional renders settle) before we look for the node.
    const timeoutId = window.setTimeout(() => {
      const element = document.getElementById(anchorId);
      if (!element) return;

      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.add(...HIGHLIGHT_CLASSES);

      const removeId = window.setTimeout(() => {
        element.classList.remove(...HIGHLIGHT_CLASSES);
      }, HIGHLIGHT_DURATION_MS);

      // Cleanup if effect runs again before the highlight finishes.
      return () => window.clearTimeout(removeId);
    }, SCROLL_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [hash, key]);
}
